import React, { useEffect, useState } from "react";
import { API, graphqlOperation, Auth } from "aws-amplify";
import { listCandidateProfiles } from "../graphql/queries";
import "./WhoAreYou.css";
import { useNavigate } from "react-router-dom";
import Header from "../components/header/Header";

const WhoAreYou = () => {
  const [activeCard, setActiveCard] = useState('candidate');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);

  const navigate = useNavigate();

  // Check authentication on page load
  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verify user is authenticated with Cognito
  async function checkAuthStatus() {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const email = currentUser.attributes.email;
      
      // User is authenticated - store verified email
      setUserEmail(email);
      setIsAuthenticated(true);
      
      // Update localStorage with verified Cognito data
      localStorage.setItem("candidateProfileEmailID", email);
      localStorage.setItem("candidateProfileAuthID", currentUser.attributes.sub);
      
      // Check for existing profile
      await checkcandidateProfile();
    } catch (error) {
      console.error("User not authenticated:", error);
      setIsAuthenticated(false);
      // Redirect to login page if not authenticated
      navigate("/login"); // Adjust this path to your login route
    } finally {
      setIsLoading(false);
    }
  }

  const CandidateDashboardClick = () => {
    if (!isAuthenticated || !userEmail) {
      navigate("/login");
      return;
    }
    navigate("/TermsPolicy");
  };

  const CompanyDashboardClick = () => {
    if (!isAuthenticated || !userEmail) {
      navigate("/login");
      return;
    }

    // Check if the email domain is @gmail.com (or other personal emails)
    const personalEmailDomains = ["@gmail.com", "@yahoo.com", "@hotmail.com", "@outlook.com"];
    const isPersonalEmail = personalEmailDomains.some(domain => userEmail.endsWith(domain));

    if (!isPersonalEmail) {
      // Business email → allowed to register company
      navigate("/CompanyRegisterForm");
    } else {
      alert("Sorry, Personal Email users cannot access the company dashboard. Please use your business email.");
    }
  };

  async function checkcandidateProfile() {
    try {
      let nextToken = null;
      let candidateProfile = null;

      do {
        const candidateProfileData = await API.graphql(
          graphqlOperation(listCandidateProfiles, {
            filter: {
              userID: {
                eq: localStorage.getItem("candidateProfileAuthID"),
              },
            },
            limit: 100,
            nextToken: nextToken,
          })
        );

        const items = candidateProfileData.data.listCandidateProfiles.items;
        if (items.length > 0) {
          candidateProfile = items[0];
          localStorage.setItem("candidateProfileId", candidateProfile.id);
          localStorage.setItem("candidateProfile", JSON.stringify(candidateProfile));
          break;
        } else {
          nextToken = candidateProfileData.data.listCandidateProfiles.nextToken;
        }
      } while (nextToken);

      if (!candidateProfile) {
        console.log("No profile found for this candidate ID.");
      }
    } catch (error) {
      console.error("Error fetching candidate profile", error);
    }
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="whoareyou-page">
        <Header />
        <div className="whoareyou-container">
          <div className="whoareyou-loading">
            <div className="loading-spinner"></div>
            <p>Verifying your session...</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="whoareyou-page">
      <Header />

      <div className="whoareyou-container">
        <div className="whoareyou-card-stack">
          {/* Candidates Card */}
          <div 
            className={`whoareyou-card candidate-card ${activeCard === 'candidate' ? 'active' : 'back'}`}
            onClick={() => activeCard !== 'candidate' && setActiveCard('candidate')}
          >
            <h2 className="whoareyou-card-title">Candidates</h2>
            
            <div className="whoareyou-card-image candidate-image">
              <div className="candidate-visual">
                <div className="ai-head-illustration">
                  <div className="floating-tag tag-1">Creative Director</div>
                  <div className="floating-tag tag-2">Project Manager</div>
                  <div className="floating-tag tag-3">UI/UX Designer</div>
                </div>
              </div>
            </div>

            <ul className="whoareyou-features">
              <li>
                <span className="check-icon">✓</span>
                AI interviews that test real skills, not just resumes.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Instant feedback to improve your answers.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Fair evaluation – no bias, no human error.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Get discovered by top companies automatically.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Match with jobs that actually fit your unique skills.
              </li>
            </ul>

            <button 
              onClick={(e) => { e.stopPropagation(); CandidateDashboardClick(); }} 
              className="whoareyou-btn"
            >
              Start as Candidate
            </button>
          </div>

          {/* Companies Card */}
          <div 
            className={`whoareyou-card company-card ${activeCard === 'company' ? 'active' : 'back'}`}
            onClick={() => activeCard !== 'company' && setActiveCard('company')}
          >
            <h2 className="whoareyou-card-title">Companies</h2>
            
            <div className="whoareyou-card-image company-image">
              <div className="company-visual">
                <div className="hologram-illustration">
                  <div className="candidate-avatars">
                    <div className="avatar-item"></div>
                    <div className="avatar-item"></div>
                    <div className="avatar-item"></div>
                    <div className="avatar-item"></div>
                  </div>
                </div>
              </div>
            </div>

            <ul className="whoareyou-features">
              <li>
                <span className="check-icon">✓</span>
                Auto-screen thousands of candidates in minutes.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Custom AI interviews tailored to your exact role.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Only see the top 5% who actually pass.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Cut time-to-hire by 80%.
              </li>
              <li>
                <span className="check-icon">✓</span>
                Seamless integration with your ATS/HR tools.
              </li>
            </ul>

            <button 
              onClick={(e) => { e.stopPropagation(); CompanyDashboardClick(); }} 
              className="whoareyou-btn"
            >
              Start as Company
            </button>
          </div>
        </div>

        {/* Right Side Toggle with Dots */}
        <div className="view-toggle-container">
          <div 
            className={`view-toggle-option ${activeCard === 'candidate' ? 'active' : ''}`}
            onClick={() => setActiveCard('candidate')}
          >
            <div className="toggle-dots">
              <span className="dot dot-1"></span>
              <span className="dot dot-2"></span>
              <span className="dot dot-3"></span>
            </div>
            <span className="toggle-label">For Candidates</span>
          </div>
          <div className="toggle-divider"></div>
          <div 
            className={`view-toggle-option ${activeCard === 'company' ? 'active' : ''}`}
            onClick={() => setActiveCard('company')}
          >
            <div className="toggle-dots">
              <span className="dot dot-1"></span>
              <span className="dot dot-2"></span>
              <span className="dot dot-3"></span>
            </div>
            <span className="toggle-label">For Companies</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhoAreYou;
